import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import type { RootState } from "../../../../redux/store";

/**
 * Custom hook to synchronize host/landlord details to URL query parameters.
 *
 * @remarks
 * - Syncs landlord first name, last name, email, and phone to URL
 * - Only syncs after service hydration to avoid premature navigation
 * - Prevents redundant URL updates using ref tracking
 * - Ensures state persists across page refreshes
 *
 * @example
 * ```tsx
 * function HostDetailsPage() {
 *   useHostDetailsUrlSync();
 *   // Landlord data now synced to URL on changes
 * }
 * ```
 */
export function useHostDetailsUrlSync(): void {
  const location = useLocation();
  const navigate = useNavigate();

  const landlordData = useSelector((state: RootState) => state.bookingCreation.landlordData);
  const selectedGroup = useSelector((state: RootState) => state.bookingCreation.selectedGroup);

  const lastSyncedDataRef = useRef<{
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  } | null>(null);

  // Only sync after we have basic data (selectedGroup indicates service hydration completed)
  const canSync = Boolean(selectedGroup);

  useEffect(() => {
    if (!canSync) return;

    // Check if data has changed since last sync
    const currentData = {
      firstName: landlordData.firstName,
      lastName: landlordData.lastName,
      email: landlordData.email,
      phone: landlordData.phone,
    };

    // If no data to sync, skip
    if (!currentData.firstName && !currentData.lastName && !currentData.email && !currentData.phone) {
      return;
    }

    // If data hasn't changed, skip
    if (
      lastSyncedDataRef.current &&
      lastSyncedDataRef.current.firstName === currentData.firstName &&
      lastSyncedDataRef.current.lastName === currentData.lastName &&
      lastSyncedDataRef.current.email === currentData.email &&
      lastSyncedDataRef.current.phone === currentData.phone
    ) {
      return;
    }

    const params = new URLSearchParams(location.search);
    let mutated = false;

    const setOrDeleteParam = (key: string, value: string) => {
      if (value) {
        if (params.get(key) !== value) {
          params.set(key, value);
          mutated = true;
        }
      } else {
        if (params.has(key)) {
          params.delete(key);
          mutated = true;
        }
      }
    };

    setOrDeleteParam("landlordFirstName", currentData.firstName);
    setOrDeleteParam("landlordLastName", currentData.lastName);
    setOrDeleteParam("landlordEmail", currentData.email);
    setOrDeleteParam("landlordPhone", currentData.phone);

    if (mutated) {
      lastSyncedDataRef.current = currentData;
      navigate(
        { pathname: location.pathname, search: `?${params.toString()}` },
        { replace: true }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    landlordData.firstName,
    landlordData.lastName,
    landlordData.email,
    landlordData.phone,
    canSync,
    location.pathname,
    navigate,
  ]);
}
